import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import HighlightText from './HighlightText';

const {
  FiCheck, FiPhoneCall, FiBookOpen, FiAward, FiUsers, FiStar,
  FiMapPin, FiClock, FiShield, FiTrendingUp, FiTarget, FiZap, FiMessageCircle,
} = FiIcons;

const FloatingIcon = ({ icon, className, position, delay = '0s', duration = '4s' }) => (
  <div className={`absolute ${position}`} style={{ animation: `float ${duration} ease-in-out infinite`, animationDelay: delay }}>
    <div className={`flex items-center justify-center rounded-2xl shadow-lg ${className}`}>
      <SafeIcon icon={icon} className="w-6 h-6" />
    </div>
  </div>
);

const Hero = () => {
  const [form, setForm] = useState({ name: '', phone: '', subject: '' });
  const [errors, setErrors] = useState({});

  const benefits = ['100% Background Verified', 'Personalized 1-on-1 Attention', 'Improve Grades Guaranteed'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.phone.trim()) newErrors.phone = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(form.phone.trim())) newErrors.phone = 'Enter a valid 10-digit mobile number';
    return newErrors;
  };

  const buildWhatsAppUrl = () => {
    const message =
      `Hello MyHomeTutors, I'm interested in a free demo.%0A%0A` +
      `Name: ${encodeURIComponent(form.name.trim())}%0A` +
      `Phone: ${encodeURIComponent(form.phone.trim())}%0A` +
      `Class/Subject: ${encodeURIComponent(form.subject.trim() || 'Not specified')}`;
    return `https://wa.me/919415905584?text=${message}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const url = buildWhatsAppUrl();

    const newTab = window.open(url, '_blank', 'noopener,noreferrer');

    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const fields = [
    { name: 'name', placeholder: 'Parent / Student Name', icon: FiUsers },
    { name: 'phone', placeholder: 'Mobile Number', icon: FiPhoneCall },
    { name: 'subject', placeholder: 'Class & Subject (e.g., 10th Math)', icon: FiBookOpen },
  ];

  return (
    <section className="relative bg-gradient-to-br from-[#fff5f5] via-[#fff8f8] to-[#ffeaea] pt-10 pb-20 lg:pb-32 overflow-hidden" id="home">
      <FloatingIcon icon={FiBookOpen} position="top-12 left-[6%]" className="w-14 h-14 bg-white text-primary opacity-70 rotate-[-12deg]" duration="5s" delay="0s" />
      <FloatingIcon icon={FiAward} position="top-20 right-[8%]" className="w-12 h-12 bg-primary text-white opacity-80 rotate-[10deg]" duration="4s" delay="1s" />
      <FloatingIcon icon={FiStar} position="top-[42%] left-[2%]" className="w-10 h-10 bg-yellow-100 text-yellow-500 opacity-60 rotate-6" duration="6s" delay="0.5s" />
      <FloatingIcon icon={FiUsers} position="bottom-[30%] left-[5%]" className="w-12 h-12 bg-blue-50 text-blue-500 opacity-60 rotate-[-8deg]" duration="4.5s" delay="2s" />
      <FloatingIcon icon={FiTarget} position="top-[30%] right-[3%]" className="w-11 h-11 bg-purple-50 text-purple-500 opacity-60 rotate-12" duration="5.5s" delay="1.5s" />
      <FloatingIcon icon={FiTrendingUp} position="bottom-[38%] right-[6%]" className="w-10 h-10 bg-green-50 text-green-500 opacity-60 rotate-[-6deg]" duration="4s" delay="0.8s" />
      <FloatingIcon icon={FiZap} position="top-[60%] left-[12%]" className="w-9 h-9 bg-orange-50 text-orange-400 opacity-50 rotate-3" duration="5s" delay="2.5s" />
      <FloatingIcon icon={FiShield} position="top-[15%] left-[20%]" className="w-10 h-10 bg-red-50 text-primary opacity-40 rotate-[-15deg]" duration="6s" delay="1.2s" />
      <FloatingIcon icon={FiClock} position="bottom-[42%] right-[15%]" className="w-9 h-9 bg-indigo-50 text-indigo-400 opacity-40 rotate-8" duration="4.5s" delay="0.3s" />

      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '28px 28px' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left Content */}
          <div className="space-y-6 pt-4 lg:pr-8">
            <div className="inline-flex items-center gap-2 bg-white border border-red-100 text-primary text-xs font-bold px-4 py-2 rounded-full shadow-sm">
              <FiStar className="w-3.5 h-3.5 fill-current text-yellow-400" />
              #1 Rated Home Tuition Service in Lucknow
            </div>
            <h1 className="text-[2.5rem] md:text-5xl lg:text-[3.8rem] font-extrabold text-slate-900 leading-[1.15] tracking-tight">
              Top-Rated <HighlightText>Home Tutors</HighlightText>
              <br className="hidden md:block" /> in Lucknow
            </h1>
            <p className="text-base text-gray-600 max-w-md leading-relaxed font-medium">
              From school exams to NEET & IIT — expert 1-on-1 home tuition for Classes 1–12, CBSE & ICSE. Personalized attention, delivered home.
            </p>
            <div className="space-y-3 pt-1">
              {benefits.map((item, idx) => (
                <div key={idx} className="flex items-center text-slate-800 font-semibold">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <FiCheck className="text-green-600 w-3 h-3 stroke-[3]" />
                  </div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={() => document.getElementById('subjects').scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 bg-primary text-white px-8 py-3.5 rounded-xl font-bold hover:bg-primary-hover transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform tracking-wide"
              >
                <FiBookOpen className="w-4 h-4" /> View Subjects
              </button>
              <a
                href="tel:+919415905584"
                className="flex items-center justify-center gap-2 bg-white text-primary border-2 border-primary px-8 py-3.5 rounded-xl font-bold hover:bg-red-50 transition-all shadow-sm tracking-wide"
              >
                <FiPhoneCall className="w-4 h-4" /> Call Us Now
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              {[
                { icon: FiUsers, text: '500+ Active Tutors' },
                { icon: FiStar, text: '4.9/5 Average Rating' },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 text-sm font-bold text-slate-700">
                  <badge.icon className="w-3.5 h-3.5 text-primary" />
                  {badge.text}
                </div>
              ))}
            </div>
          </div>

          {/* Right Lead Form */}
          <div className="relative w-full max-w-md mx-auto lg:ml-auto z-20 mt-8 lg:mt-0">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl border border-red-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-orange-400 to-yellow-400 rounded-t-2xl"></div>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center shadow-sm">
                  <FiMessageCircle className="text-primary w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-slate-900 tracking-tight leading-tight">Book a Free Demo</h3>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">We'll reply on WhatsApp instantly</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {fields.map((field) => (
                  <div key={field.name}>
                    <div className="relative">
                      <field.icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type={field.name === 'phone' ? 'tel' : 'text'}
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        maxLength={field.name === 'phone' ? 10 : undefined}
                        className={`w-full pl-11 pr-4 py-3.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50 text-slate-700 text-sm placeholder-gray-400 transition font-medium ${
                          errors[field.name] ? 'border-red-400 bg-red-50' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors[field.name] && (
                      <p className="text-xs text-red-500 font-semibold mt-1 pl-1">{errors[field.name]}</p>
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  className="w-full bg-primary text-white py-4 rounded-xl font-extrabold hover:bg-primary-hover transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform text-base mt-2 flex items-center justify-center gap-2 tracking-wide"
                >
                  <FiPhoneCall className="w-4 h-4" /> Request Free Callback
                </button>
              </form>

              <p className="text-center text-xs text-gray-400 font-medium mt-3">
                Or{' '}
                <a
                  href={`https://wa.me/919415905584?text=Hello%20MyHomeTutors%2C%20I%27m%20interested%20in%20a%20free%20demo.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-bold hover:underline"
                >
                  chat directly on WhatsApp
                </a>
              </p>

              <div className="mt-4 flex items-center justify-center gap-4">
                <div className="flex -space-x-2">
                  <img className="w-6 h-6 rounded-full border-2 border-white" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=64&q=80" alt="User" />
                  <img className="w-6 h-6 rounded-full border-2 border-white" src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=64&q=80" alt="User" />
                  <img className="w-6 h-6 rounded-full border-2 border-white" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=64&q=80" alt="User" />
                </div>
                <p className="text-xs text-gray-500 font-medium">Joined by <span className="text-slate-800 font-bold">3,000+</span> parents</p>
              </div>

              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-red-50 rounded-full blur-2xl -z-10"></div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;